import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { useTQPrefetchTokenRegistry } from 'contexts/TQPrefetchTokenContext';
import { getRequest, postRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID, UpdateRecordsRequest } from 'types';
import { DataFieldValue, DataFieldValueUpdateMeta, GetDataFieldValuesQueryParams } from 'types/dataFieldValue';
import type { PropertyFilterNode } from 'services/SPM/SPMFilteredViewService';
import type { ServerFilterExpressionNode } from 'types/accountDealer';
import { APIError, handlePrefetchQueryError } from 'utils/errors';
import { CSRFService } from 'services/csrf';
import { QueryHookOptions } from 'types/ReactQuery';
import { QueryEffects, useQueryEffects_deprecated } from 'utils/useQueryEffects_deprecated';

type DataFieldValuesListResponse = APIList<DataFieldValue> & { scope_user_row_ids?: string[] };

/** Query key index 3. Exported so callers can match the key without duplicating the literal. */
export const DATA_FIELD_VALUES_KEY_SEGMENT = 'data-field-values';

type ApiUrlOptions = {
  modelId: StringifiedUUID;
  dataObjectId: StringifiedUUID;
  proposedChangeGroupId?: string;
  prefetchToken?: string;
};
const apiUrl = ({ modelId, dataObjectId, proposedChangeGroupId, prefetchToken }: ApiUrlOptions) => {
  const searchParams = new URLSearchParams();
  if (proposedChangeGroupId) {
    searchParams.set('change_group_id', proposedChangeGroupId);
  }
  if (prefetchToken) {
    searchParams.set('prefetch_token', prefetchToken);
  }

  let queryParams = searchParams.toString();
  queryParams = queryParams ? `?${queryParams}` : '';

  return `/api/v1/tq/hierarchy-models/${modelId}/data-object-schemas/${dataObjectId}/data-field-values/${queryParams}`;
};

// Server side params with stringified json column filters and snake_case params
export type BackendGetDataFieldValuesQueryParams = {
  limit?: number;
  offset?: number;
  territory_id?: string;
  territory_view_id?: string;
  column_filter?: string;
  sort?: string;
  for_compare?: boolean;
  prefetch_token?: string;
  approval_group_uuid?: string;
};

export const toBackEndQueryParams = ({
  territoryId,
  territoryViewId,
  columnFilter,
  limit,
  page,
  offset,
  recordId,
  sortColumnUuid,
  sortAscending,
  prefetchToken,
  proposedChangeGroupId,
  scopeUserId,
  approvalGroupUuid,
}: GetDataFieldValuesQueryParams): BackendGetDataFieldValuesQueryParams => {
  const encodedColumnFilter = columnFilter ? encodeURIComponent(JSON.stringify(columnFilter)) : undefined;
  const snakeCaseParams: { [key: string]: string | number } = {};
  const insertDefined = (key: string, val: string | number | undefined | null) => {
    if (val !== undefined && val !== null) {
      snakeCaseParams[key] = val;
    }
  };
  insertDefined('territory_id', territoryId);
  if (territoryViewId !== undefined) {
    snakeCaseParams.territory_view_id = territoryViewId ?? '';
  }
  insertDefined('limit', limit);
  insertDefined('column_filters', encodedColumnFilter);
  if (offset !== undefined) {
    insertDefined('offset', offset);
  }
  if (page !== undefined) {
    insertDefined('page', page);
  }
  if (recordId) {
    insertDefined('record_id', recordId);
  }
  if (sortColumnUuid) {
    const sortOrder = sortAscending ? 1 : 0;
    const sortJson = JSON.stringify({ order: [[sortColumnUuid, sortOrder]] });
    insertDefined('sort', encodeURIComponent(sortJson));
  }
  insertDefined('change_group_id', proposedChangeGroupId);
  if (prefetchToken) {
    insertDefined('prefetch_token', prefetchToken);
  }
  insertDefined('scope_user_id', scopeUserId);
  insertDefined('approval_group_uuid', approvalGroupUuid);
  return snakeCaseParams;
};

// Direct data field values retrieval function
export const getDataFieldValues = (
  modelId: StringifiedUUID,
  dataObjectId: StringifiedUUID,
  queryParams?: GetDataFieldValuesQueryParams,
) => {
  const effectiveQueryParams = queryParams ?? {};
  return getRequest<BackendGetDataFieldValuesQueryParams, DataFieldValuesListResponse>(
    apiUrl({ modelId, dataObjectId }),
    toBackEndQueryParams(effectiveQueryParams),
  );
};
// TODO: remove this when compare models gets revamped
export const getDataFieldValuesForCompare = (
  modelId: StringifiedUUID,
  dataObjectId: StringifiedUUID,
  queryParams?: GetDataFieldValuesQueryParams,
) => {
  const effectiveQueryParams = queryParams ?? {};
  const backendQueryParams = toBackEndQueryParams(effectiveQueryParams);
  backendQueryParams.for_compare = true;

  return getRequest<BackendGetDataFieldValuesQueryParams, APIList<DataFieldValue>>(
    apiUrl({ modelId, dataObjectId }),
    backendQueryParams,
  );
};

const useGetDataFieldsValueQuery = (
  modelId: StringifiedUUID,
  dataObjectSchemaId: StringifiedUUID,
  queryParams?: GetDataFieldValuesQueryParams,
  options: QueryHookOptions<DataFieldValuesListResponse, APIError> &
    QueryEffects<DataFieldValuesListResponse, APIError> = {},
) => {
  const { clearToken, generateTokenKey, getTokenFromParams, getPrefetchAttempts, resetPrefetchAttempts } =
    useTQPrefetchTokenRegistry();

  const getEffectivePrefetchToken = () => {
    // User provided token takes precedence
    if (queryParams?.prefetchToken) {
      return queryParams.prefetchToken;
    }

    // Don't try to lookup token if required params are missing
    // This prevents looking up with wrong key (e.g., empty dataObjectSchemaId)
    if (!modelId || !dataObjectSchemaId) {
      return undefined;
    }

    // Persisted token from registry
    return getTokenFromParams({
      modelId,
      dataObjectSchemaId,
      ...queryParams,
    });
  };

  const effectivePrefetchToken = getEffectivePrefetchToken();
  const effectiveQueryParams = {
    ...queryParams,
    prefetchToken: effectivePrefetchToken,
  };

  const {
    columnFilter,
    limit,
    offset,
    page,
    territoryId,
    territoryViewId,
    recordId,
    sortColumnUuid,
    sortAscending,
    prefetchToken,
    scopeUserId,
    approvalGroupUuid,
  } = effectiveQueryParams;

  const QUERY_KEY = [
    'hierarchy-models',
    modelId,
    dataObjectSchemaId,
    DATA_FIELD_VALUES_KEY_SEGMENT,
    limit,
    offset,
    page,
    territoryId,
    territoryViewId,
    recordId,
    columnFilter,
    sortColumnUuid,
    sortAscending,
    scopeUserId,
    prefetchToken ? `prefetch-token-${prefetchToken}` : undefined,
    // Keyed even though it is excluded from the prefetch token key (see NON_KEY_QUERY_PARAMS in
    // TQPrefetchTokenContext). For a collaborator with a filtered view the response genuinely
    // differs — `filter_values_for_filtered_view_set` widens the surviving columns to include
    // validation-rule columns — and the client cannot tell whether the current user is restricted.
    // Omitting it would serve those users a cached pre-approval response with the validation
    // column filtered out, and validation state would silently pass. The cost is one refetch on
    // approval entry for unrestricted users.
    approvalGroupUuid,
  ];

  const queryClient = useQueryClient();

  const isQueryEnabled =
    !!modelId && dataObjectSchemaId.length > 0 && !!effectivePrefetchToken && (options.enabled ?? true);
  const { onSuccess, onError, onSettled, ...queryOptions } = options;
  const query = useQuery<DataFieldValuesListResponse, APIError>({
    ...queryOptions,
    // Only run this query if:
    // 1. modelId and dataObjectId are valid
    // 2. Prefetch is disabled OR prefetch token is available
    // 3. Caller wants it enabled
    enabled: isQueryEnabled,
    // Disable React Query's built-in retry for prefetch queries
    // We handle retries manually by fetching new prefetch tokens
    retry: false,
    queryKey: QUERY_KEY,
    queryFn: () => {
      // Re-check token validity at execution time to handle race conditions
      // Token may have been cleared between query creation and execution
      const tokenAtExecution = getEffectivePrefetchToken();
      if (!tokenAtExecution) {
        // Return empty result to silently cancel this query
        return Promise.resolve({ total_count: 0, data: [] });
      }

      return getDataFieldValues(modelId, dataObjectSchemaId, {
        ...effectiveQueryParams,
        prefetchToken: tokenAtExecution,
      });
    },
  });
  useQueryEffects_deprecated(query, {
    onSuccess: (data) => {
      onSuccess?.(data);

      // Reset prefetch attempts on successful data fetch
      if (effectivePrefetchToken) {
        const { prefetchToken: _token, ...paramsWithoutToken } = effectiveQueryParams;
        const tokenKey = generateTokenKey({ modelId, dataObjectSchemaId, ...paramsWithoutToken });
        resetPrefetchAttempts(tokenKey);
      }
    },
    onError: (error) => {
      const { prefetchToken: _token, ...paramsWithoutToken } = effectiveQueryParams;
      const tokenKey = generateTokenKey({ modelId, dataObjectSchemaId, ...paramsWithoutToken });
      handlePrefetchQueryError(error, {
        token: effectivePrefetchToken,
        attempts: getPrefetchAttempts(tokenKey),
        clearToken: () => clearToken(tokenKey),
        onError,
      });
    },
    onSettled,
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  const cancelQuery = () => queryClient.cancelQueries({ queryKey: QUERY_KEY, exact: true });
  return {
    query,
    invalidateQuery,
    cancelQuery,
  };
};

export type TQSharedViewPreviewParams = {
  modelId: StringifiedUUID;
  dataObjectSchemaId: StringifiedUUID;
  scenarioId?: string | null;
  collaboratorId?: string;
  filterExpression?: ServerFilterExpressionNode<PropertyFilterNode> | null;
  columnIds?: StringifiedUUID[];
};

export type TQSharedViewPreviewRecord = { id: string; data: Record<string, string | null> };

const useSharedViewPreviewQuery = ({
  modelId,
  dataObjectSchemaId,
  scenarioId,
  collaboratorId,
  filterExpression,
  columnIds,
}: TQSharedViewPreviewParams) => {
  const { getTokenForSchema } = useTQPrefetchTokenRegistry();
  const prefetchToken = getTokenForSchema(modelId, dataObjectSchemaId);
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  const QUERY_KEY = [
    'tq-shared-view-preview',
    modelId,
    dataObjectSchemaId,
    scenarioId,
    collaboratorId,
    filterExpression ? JSON.stringify(filterExpression) : null,
    columnIds,
  ];

  const query = useQuery<APIList<TQSharedViewPreviewRecord>, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => {
      const url = `/api/v1/tq/hierarchy-models/${modelId}/data-object-schemas/${dataObjectSchemaId}/data-field-values/shared-view-preview/?prefetch_token=${prefetchToken}`;
      return postRequest<Record<string, unknown>, APIList<TQSharedViewPreviewRecord>>(
        url,
        {
          scenario_id: scenarioId,
          collaborator_id: collaboratorId,
          filter_expression: filterExpression ?? null,
          column_ids: columnIds ?? [],
        },
        csrfToken,
      );
    },
    enabled: !!modelId && !!dataObjectSchemaId && !!scenarioId && !!prefetchToken && !!csrfToken,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  return { query };
};

type UpdateDateFieldValueOptions = {
  modelId: StringifiedUUID;
  dataObjectId: StringifiedUUID;
  csrfToken: string;
  proposedChangeGroupId?: string;
  prefetchToken?: string;
};
const updateDataFieldValue =
  ({ modelId, dataObjectId, csrfToken, proposedChangeGroupId, prefetchToken }: UpdateDateFieldValueOptions) =>
  (data: UpdateRecordsRequest<DataFieldValueUpdateMeta>) =>
    postRequest<UpdateRecordsRequest<DataFieldValueUpdateMeta>, APIList<DataFieldValue>>(
      apiUrl({ modelId, dataObjectId, proposedChangeGroupId, prefetchToken }),
      data,
      csrfToken,
    );

type UpdateMutationOptions = {
  modelId: StringifiedUUID;
  dataObjectId: StringifiedUUID;
  proposedChangeGroupId?: string;
  prefetchToken?: string;
  options?: UseMutationOptions<APIList<DataFieldValue>, APIError, UpdateRecordsRequest<DataFieldValueUpdateMeta>>;
};
const useUpdateDataFieldValueMutation = ({
  modelId,
  dataObjectId,
  proposedChangeGroupId,
  prefetchToken,
  options = {},
}: UpdateMutationOptions) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<APIList<DataFieldValue>, APIError, UpdateRecordsRequest<DataFieldValueUpdateMeta>>({
    ...options,
    mutationFn: updateDataFieldValue({ modelId, dataObjectId, csrfToken, proposedChangeGroupId, prefetchToken }),
  });
};

export const DataFieldsValueService = {
  apiUrl,
  getDataFieldValues,
  getDataFieldValuesForCompare,
  useGetDataFieldsValueQuery,
  useSharedViewPreviewQuery,
  useUpdateDataFieldValueMutation,
};
